import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Checkbox } from '../ui/checkbox';
import { toast } from 'sonner';
import { 
  ArrowLeft, Send, Eye, FileText, Users, Code, CheckCircle, XCircle, Table, Grid3X3, Home
} from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';
import ReactQuill, { Quill } from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import BlotFormatter from 'quill-blot-formatter';

// Register custom fonts with Quill
const Font = Quill.import('formats/font');
Font.whitelist = ['Arial', 'Verdana', 'Georgia', 'Times New Roman', 'Trebuchet MS', 'Courier New'];
Quill.register(Font, true);

// Register blot formatter for image resizing
Quill.register('modules/blotFormatter', BlotFormatter);

// Custom fonts for the email editor
const FONTS = ['Arial', 'Verdana', 'Georgia', 'Times New Roman', 'Trebuchet MS', 'Courier New'];

const SendEmail = () => {
  const navigate = useNavigate();
  const { bookingId } = useParams();
  const [searchParams] = useSearchParams();
  
  // Get pre-selected customer IDs from URL
  const preSelectedCustomers = searchParams.get('customers')?.split(',').filter(Boolean) || [];
  
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  
  const [templates, setTemplates] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [booking, setBooking] = useState(null);
  const [placeholders, setPlaceholders] = useState([]);
  
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [selectedCustomers, setSelectedCustomers] = useState(preSelectedCustomers);
  const [selectedAttachments, setSelectedAttachments] = useState([]);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  
  const [preview, setPreview] = useState(null);
  const [sendResult, setSendResult] = useState(null);
  
  // Refs for cursor position insertion
  const quillRef = useRef(null);
  const subjectRef = useRef(null);
  const mentionDropdownRef = useRef(null);
  const [insertTarget, setInsertTarget] = useState('body'); // 'body' or 'subject'
  
  // @ mention autocomplete state
  const [showMentions, setShowMentions] = useState(false);
  const [mentionFilter, setMentionFilter] = useState('');
  const [mentionPosition, setMentionPosition] = useState({ top: 0, left: 0 });
  const [mentionSource, setMentionSource] = useState('body'); // 'body' or 'subject'
  const [mentionStartIndex, setMentionStartIndex] = useState(0);
  const [selectedMentionIndex, setSelectedMentionIndex] = useState(0);
  
  // Table insertion modal state
  const [showTableModal, setShowTableModal] = useState(false);
  const [tableConfig, setTableConfig] = useState({
    rows: 3,
    cols: 3,
    showBorders: true,
    columnWidths: ''
  });
  const [editingTable, setEditingTable] = useState(null); // For editing existing tables

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const isBookingContext = !!bookingId;

  const modules = useMemo(() => ({
    toolbar: {
      container: [
        [{ 'font': FONTS }],
        [{ 'header': [1, 2, 3, false] }],
        [{ 'size': ['small', false, 'large', 'huge'] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ 'color': [] }, { 'background': [] }],
        [{ 'list': 'ordered'}, { 'list': 'bullet' }],
        [{ 'align': [] }],
        ['link', 'image', 'insertTable'],
        ['clean']
      ],
      handlers: {
        insertTable: function() {
          // This will be handled by our custom button
        }
      }
    },
    blotFormatter: {}
  }), []);

  const formats = [
    'font', 'header', 'size', 'bold', 'italic', 'underline', 'strike',
    'color', 'background', 'list', 'bullet', 'align', 'link', 'image',
    'width', 'height', 'style'
  ];

  // Insert table into editor
  const insertTable = () => {
    const { rows, cols, showBorders, columnWidths } = tableConfig;
    
    // Parse column widths (e.g., "100,200,150" or "33%,33%,34%")
    const widths = columnWidths.split(',').map(w => w.trim()).filter(Boolean);
    
    // Build table HTML with inline styles (essential for email compatibility)
    const borderStyle = showBorders ? '1px solid #cccccc' : 'none';
    let tableHtml = `<table style="border-collapse: collapse; width: 100%; border: ${borderStyle};" data-border="${showBorders ? '1' : '0'}">`;
    
    for (let r = 0; r < rows; r++) {
      tableHtml += '<tr>';
      for (let c = 0; c < cols; c++) {
        const width = widths[c] || '';
        const widthStyle = width ? ` width: ${width.includes('%') ? width : width + 'px'};` : '';
        tableHtml += `<td style="border: ${borderStyle}; padding: 8px;${widthStyle}">&nbsp;</td>`;
      }
      tableHtml += '</tr>';
    }
    tableHtml += '</table>';
    
    // Get Quill editor and insert directly into its root
    const quill = quillRef.current?.getEditor();
    if (quill) {
      // Get current HTML content
      const currentHtml = quill.root.innerHTML;
      
      if (editingTable) {
        // Replace existing table
        quill.root.innerHTML = currentHtml.replace(editingTable, tableHtml);
        setEditingTable(null);
      } else {
        // Append table to the end
        quill.root.innerHTML = currentHtml + tableHtml + '<p><br></p>';
      }
      
      // Sync state with the new content
      setBody(quill.root.innerHTML);
    }
    
    setShowTableModal(false);
    setTableConfig({ rows: 3, cols: 3, showBorders: true, columnWidths: '' });
    toast.success(editingTable ? 'Table updated' : 'Table inserted');
  };

  // Edit existing table - check Quill's actual HTML content
  const handleEditTable = () => {
    const quill = quillRef.current?.getEditor();
    if (!quill) {
      toast.error('Editor not ready');
      return;
    }
    
    const currentHtml = quill.root.innerHTML;
    const tableMatch = currentHtml.match(/<table[^>]*>[\s\S]*?<\/table>/i);
    
    if (tableMatch) {
      const tableHtml = tableMatch[0];
      setEditingTable(tableHtml);
      
      // Parse table properties
      const hasBorder = tableHtml.includes('data-border="1"') || (tableHtml.includes('border:') && !tableHtml.includes('border: none'));
      const rowMatches = tableHtml.match(/<tr/gi);
      const firstRowCols = tableHtml.match(/<tr[^>]*>[\s\S]*?<\/tr>/i);
      const colMatches = firstRowCols ? firstRowCols[0].match(/<td/gi) : null;
      
      setTableConfig({
        rows: rowMatches ? rowMatches.length : 3,
        cols: colMatches ? colMatches.length : 3,
        showBorders: hasBorder,
        columnWidths: ''
      });
      setShowTableModal(true);
    } else {
      toast.error('No table found in the email body');
    }
  };

  // Toggle borders on existing table
  const toggleTableBorders = () => {
    const quill = quillRef.current?.getEditor();
    if (!quill) return;
    
    const currentHtml = quill.root.innerHTML;
    const tableMatch = currentHtml.match(/<table[^>]*>[\s\S]*?<\/table>/i);
    
    if (!tableMatch) {
      toast.error('No table found in the email body');
      return;
    }
    
    let tableHtml = tableMatch[0];
    const hasBorders = tableHtml.includes('data-border="1"');
    
    if (hasBorders) {
      // Remove borders
      tableHtml = tableHtml
        .replace(/data-border="1"/g, 'data-border="0"')
        .replace(/border:\s*1px solid #[a-fA-F0-9]+;?/g, 'border: none;')
        .replace(/border:\s*1px solid [^;]+;?/g, 'border: none;');
    } else {
      // Add borders
      tableHtml = tableHtml
        .replace(/data-border="0"/g, 'data-border="1"')
        .replace(/border:\s*none;?/g, 'border: 1px solid #cccccc;');
    }
    
    quill.root.innerHTML = currentHtml.replace(tableMatch[0], tableHtml);
    setBody(quill.root.innerHTML);
    toast.success(hasBorders ? 'Borders removed' : 'Borders added');
  };

  // Add row to existing table
  const addTableRow = () => {
    const quill = quillRef.current?.getEditor();
    if (!quill) return;
    
    const currentHtml = quill.root.innerHTML;
    const tableMatch = currentHtml.match(/<table[^>]*>([\s\S]*?)<\/table>/i);
    
    if (!tableMatch) {
      toast.error('No table found');
      return;
    }
    
    // Find first row to copy structure
    const firstRowMatch = tableMatch[1].match(/<tr[^>]*>([\s\S]*?)<\/tr>/i);
    if (firstRowMatch) {
      // Create new row with same number of cells
      const cellMatches = firstRowMatch[1].match(/<td[^>]*>[\s\S]*?<\/td>/gi);
      if (cellMatches) {
        const hasBorders = tableMatch[0].includes('data-border="1"');
        const borderStyle = hasBorders ? '1px solid #cccccc' : 'none';
        const newRow = '<tr>' + cellMatches.map(() => 
          `<td style="border: ${borderStyle}; padding: 8px;">&nbsp;</td>`
        ).join('') + '</tr>';
        
        const newTableContent = tableMatch[1] + newRow;
        const newTable = tableMatch[0].replace(tableMatch[1], newTableContent);
        quill.root.innerHTML = currentHtml.replace(tableMatch[0], newTable);
        setBody(quill.root.innerHTML);
        toast.success('Row added');
      }
    }
  };

  // Add column to existing table
  const addTableColumn = () => {
    const quill = quillRef.current?.getEditor();
    if (!quill) return;
    
    const currentHtml = quill.root.innerHTML;
    const tableMatch = currentHtml.match(/<table[^>]*>([\s\S]*?)<\/table>/i);
    
    if (!tableMatch) {
      toast.error('No table found');
      return;
    }
    
    const hasBorders = tableMatch[0].includes('data-border="1"');
    const borderStyle = hasBorders ? '1px solid #cccccc' : 'none';
    const newCell = `<td style="border: ${borderStyle}; padding: 8px;">&nbsp;</td>`;
    
    // Add cell to each row
    let newContent = tableMatch[1].replace(/<\/tr>/gi, newCell + '</tr>');
    const newTable = tableMatch[0].replace(tableMatch[1], newContent);
    quill.root.innerHTML = currentHtml.replace(tableMatch[0], newTable);
    setBody(quill.root.innerHTML);
    toast.success('Column added');
  };

  // Setup custom toolbar button for table
  useEffect(() => {
    // Need a small delay to ensure Quill toolbar is rendered
    const timer = setTimeout(() => {
      const toolbarButton = document.querySelector('.ql-insertTable');
      if (toolbarButton) {
        toolbarButton.innerHTML = '<svg viewBox="0 0 24 24" width="18" height="18"><path fill="currentColor" d="M3 3h18v18H3V3zm16 16V5H5v14h14zM7 7h4v4H7V7zm6 0h4v4h-4V7zm-6 6h4v4H7v-4zm6 0h4v4h-4v-4z"/></svg>';
        toolbarButton.title = 'Insert Table';
        toolbarButton.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          setShowTableModal(true);
        });
      }
    }, 500);
    
    return () => clearTimeout(timer);
  }, [loading]);
  
  // Filter placeholders based on mention filter and context
  const filteredPlaceholders = useMemo(() => {
    let allPlaceholders = placeholders;
    if (!isBookingContext) {
      allPlaceholders = placeholders.filter(p => p.category === 'customer');
    }
    if (!mentionFilter) return allPlaceholders;
    const filter = mentionFilter.toLowerCase();
    return allPlaceholders.filter(p => 
      p.display_name?.toLowerCase().includes(filter) ||
      p.description?.toLowerCase().includes(filter) ||
      p.placeholder?.toLowerCase().includes(filter)
    );
  }, [placeholders, mentionFilter, isBookingContext]);

  // Handle @ key in subject input
  const handleSubjectKeyDown = (e) => {
    if (showMentions && mentionSource === 'subject') {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedMentionIndex(prev => Math.min(prev + 1, filteredPlaceholders.length - 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedMentionIndex(prev => Math.max(prev - 1, 0));
      } else if (e.key === 'Enter' && filteredPlaceholders.length > 0) {
        e.preventDefault();
        insertMentionInSubject(filteredPlaceholders[selectedMentionIndex]);
      } else if (e.key === 'Escape') {
        setShowMentions(false);
      }
    }
  };

  const handleSubjectChange = (e) => {
    const value = e.target.value;
    const cursorPos = e.target.selectionStart;
    setSubject(value);
    
    // Check for @ trigger
    const textBeforeCursor = value.substring(0, cursorPos);
    const atIndex = textBeforeCursor.lastIndexOf('@');
    
    if (atIndex !== -1) {
      const textAfterAt = textBeforeCursor.substring(atIndex + 1);
      // Only show if @ is at start or preceded by space, and no space after @
      const charBeforeAt = atIndex > 0 ? value[atIndex - 1] : ' ';
      if ((charBeforeAt === ' ' || atIndex === 0) && !textAfterAt.includes(' ')) {
        setMentionFilter(textAfterAt);
        setMentionStartIndex(atIndex);
        setMentionSource('subject');
        setSelectedMentionIndex(0);
        
        // Position dropdown below the input
        const input = subjectRef.current;
        if (input) {
          const rect = input.getBoundingClientRect();
          setMentionPosition({ top: rect.bottom + 5, left: rect.left });
        }
        setShowMentions(true);
        return;
      }
    }
    setShowMentions(false);
  };

  const insertMentionInSubject = (placeholder) => {
    const before = subject.substring(0, mentionStartIndex);
    const after = subject.substring(subjectRef.current?.selectionStart || subject.length);
    const newValue = before + placeholder.placeholder + after;
    setSubject(newValue);
    setShowMentions(false);
    
    // Focus and set cursor position
    setTimeout(() => {
      const input = subjectRef.current;
      if (input) {
        input.focus();
        const newPos = mentionStartIndex + placeholder.placeholder.length;
        input.setSelectionRange(newPos, newPos);
      }
    }, 0);
  };

  // Handle @ in Quill editor
  const handleQuillChange = (content, delta, source, editor) => {
    setBody(content);
    
    if (source !== 'user') return;
    
    const quill = quillRef.current?.getEditor();
    if (!quill) return;
    
    const selection = quill.getSelection();
    if (!selection) return;
    
    const text = quill.getText(0, selection.index);
    const atIndex = text.lastIndexOf('@');
    
    if (atIndex !== -1) {
      const textAfterAt = text.substring(atIndex + 1);
      const charBeforeAt = atIndex > 0 ? text[atIndex - 1] : '\n';
      
      // Only show if @ is at start or preceded by space/newline, and no space after @
      if ((charBeforeAt === ' ' || charBeforeAt === '\n' || atIndex === 0) && !textAfterAt.includes(' ') && !textAfterAt.includes('\n')) {
        setMentionFilter(textAfterAt);
        setMentionStartIndex(atIndex);
        setMentionSource('body');
        setSelectedMentionIndex(0);
        
        // Get position from Quill
        const bounds = quill.getBounds(selection.index);
        const editorRect = quill.container.getBoundingClientRect();
        setMentionPosition({ 
          top: editorRect.top + bounds.top + bounds.height + 5,
          left: editorRect.left + bounds.left
        });
        setShowMentions(true);
        return;
      }
    }
    setShowMentions(false);
  };

  const handleQuillKeyDown = (e) => {
    if (showMentions && mentionSource === 'body') {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedMentionIndex(prev => Math.min(prev + 1, filteredPlaceholders.length - 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedMentionIndex(prev => Math.max(prev - 1, 0));
      } else if (e.key === 'Enter' && filteredPlaceholders.length > 0) {
        e.preventDefault();
        insertMentionInBody(filteredPlaceholders[selectedMentionIndex]);
      } else if (e.key === 'Escape') {
        setShowMentions(false);
      }
    }
  };

  const insertMentionInBody = (placeholder) => {
    const quill = quillRef.current?.getEditor();
    if (!quill) return;
    
    const selection = quill.getSelection();
    if (!selection) return;
    
    // Delete the @mention text and insert placeholder
    const deleteLength = selection.index - mentionStartIndex;
    quill.deleteText(mentionStartIndex, deleteLength);
    quill.insertText(mentionStartIndex, placeholder.placeholder);
    quill.setSelection(mentionStartIndex + placeholder.placeholder.length);
    
    setShowMentions(false);
  };

  // Close mentions dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (mentionDropdownRef.current && !mentionDropdownRef.current.contains(e.target)) {
        setShowMentions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const promises = [
        fetch(`${API_URL}/api/email-templates${!isBookingContext ? '?exclude_booking_templates=true' : ''}`, { credentials: 'include' }),
        fetch(`${API_URL}/api/attachments`, { credentials: 'include' }),
        fetch(`${API_URL}/api/customers`, { credentials: 'include' }),
        fetch(`${API_URL}/api/email-templates/placeholders`, { credentials: 'include' })
      ];

      if (bookingId) {
        promises.push(fetch(`${API_URL}/api/bookings/${bookingId}`, { credentials: 'include' }));
      }

      const responses = await Promise.all(promises);
      
      if (responses[0].ok) setTemplates(await responses[0].json());
      if (responses[1].ok) setAttachments(await responses[1].json());
      if (responses[2].ok) {
        const customersData = await responses[2].json();
        setCustomers(customersData);
        
        // If booking context, select the booking's customer
        if (bookingId && responses[4]?.ok) {
          const bookingData = await responses[4].json();
          setBooking(bookingData);
          setSelectedCustomers([bookingData.customer_id]);
        }
      }
      if (responses[3].ok) setPlaceholders(await responses[3].json());
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, [API_URL, bookingId, isBookingContext]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Load template when selected
  useEffect(() => {
    if (selectedTemplate) {
      const template = templates.find(t => t.template_id === selectedTemplate);
      if (template) {
        setSubject(template.subject);
        setBody(template.body);
        setSelectedAttachments(template.default_attachments || []);
      }
    }
  }, [selectedTemplate, templates]);

  const handlePreview = async () => {
    if (selectedCustomers.length === 0) {
      toast.error('Please select at least one recipient');
      return;
    }

    if (!subject || !body) {
      toast.error('Please provide subject and body');
      return;
    }

    setPreviewing(true);
    try {
      const response = await fetch(`${API_URL}/api/admin-emails/preview`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          template_id: selectedTemplate || null,
          customer_id: selectedCustomers[0],
          booking_id: bookingId || null,
          subject,
          body
        })
      });

      if (response.ok) {
        const data = await response.json();
        setPreview(data);
      } else {
        toast.error('Failed to generate preview');
      }
    } catch (error) {
      console.error('Error previewing:', error);
      toast.error('Failed to generate preview');
    } finally {
      setPreviewing(false);
    }
  };

  const handleSend = async () => {
    if (selectedCustomers.length === 0) {
      toast.error('Please select at least one recipient');
      return;
    }

    if (!subject || !body) {
      toast.error('Please provide subject and body');
      return;
    }

    setSending(true);
    setSendResult(null);
    try {
      const response = await fetch(`${API_URL}/api/admin-emails/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          template_id: selectedTemplate || null,
          customer_ids: selectedCustomers,
          booking_id: bookingId || null,
          subject,
          body,
          attachment_ids: selectedAttachments
        })
      });

      if (response.ok) {
        const result = await response.json();
        setSendResult(result);
        
        if (result.success.length > 0) {
          toast.success(`Email sent to ${result.success.length} recipient(s)`);
        }
        if (result.failed.length > 0) {
          toast.error(`Failed to send to ${result.failed.length} recipient(s)`);
        }
      } else {
        toast.error('Failed to send emails');
      }
    } catch (error) {
      console.error('Error sending:', error);
      toast.error('Failed to send emails');
    } finally {
      setSending(false);
    }
  };

  const toggleCustomer = (customerId) => {
    if (isBookingContext) return; // Can't change customer in booking context
    
    setSelectedCustomers(prev =>
      prev.includes(customerId)
        ? prev.filter(id => id !== customerId)
        : [...prev, customerId]
    );
  };

  const selectAllCustomers = () => {
    if (isBookingContext) return;
    setSelectedCustomers(customers.map(c => c.customer_id));
  };

  const deselectAllCustomers = () => {
    if (isBookingContext) return;
    setSelectedCustomers([]);
  };

  const insertPlaceholder = (placeholder) => {
    if (insertTarget === 'subject') {
      // Insert into subject at cursor position
      const input = subjectRef.current;
      if (input) {
        const start = input.selectionStart || 0;
        const end = input.selectionEnd || 0;
        const newValue = subject.substring(0, start) + placeholder + subject.substring(end);
        setSubject(newValue);
        // Restore cursor position after the inserted placeholder
        setTimeout(() => {
          input.focus();
          input.setSelectionRange(start + placeholder.length, start + placeholder.length);
        }, 0);
      } else {
        setSubject(prev => prev + placeholder);
      }
    } else {
      // Insert into body at cursor position using Quill
      const quill = quillRef.current?.getEditor();
      if (quill) {
        const range = quill.getSelection();
        if (range) {
          quill.insertText(range.index, placeholder);
          quill.setSelection(range.index + placeholder.length);
        } else {
          // No selection, insert at end
          const length = quill.getLength();
          quill.insertText(length - 1, placeholder);
        }
      } else {
        setBody(prev => prev + placeholder);
      }
    }
    toast.success(`Inserted ${placeholder}`);
  };

  const customerPlaceholders = placeholders.filter(p => p.category === 'customer');
  const bookingPlaceholders = placeholders.filter(p => p.category === 'booking');

  if (loading) {
    return (
      <div className="min-h-screen bg-[#1a1f1a] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#c9a55a]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#1a1f1a]" data-testid="send-email-page">
      <header className="bg-[#2d5f3f] shadow-lg">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center gap-4">
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/admin')}
              className="text-[#f5f3ed] hover:bg-[#1a1f1a]/20"
              title="Home"
            >
              <Home className="w-5 h-5" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate(bookingId ? `/admin/bookings/${bookingId}` : '/admin')}
              className="text-[#f5f3ed] hover:bg-[#1a1f1a]/20"
              title="Back"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
          </div>
          <div>
            <h1 className="text-2xl font-bold text-[#f5f3ed]">Send Email</h1>
            {booking && (
              <p className="text-[#c9a55a] text-sm">
                Booking: {booking.display_id || booking.booking_id} - {booking.customer_name}
              </p>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 xl:grid-cols-4 gap-8">
          {/* Main Content */}
          <div className="xl:col-span-3 space-y-6">
            {/* Recipients */}
            {!isBookingContext && (
              <div className="bg-[#2d5f3f]/30 rounded-xl p-6 border border-[#2d5f3f]">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold text-[#f5f3ed] flex items-center gap-2">
                    <Users className="w-5 h-5 text-[#c9a55a]" />
                    Recipients ({selectedCustomers.length} selected)
                  </h2>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={selectAllCustomers}
                      className="border-[#c9a55a] text-[#c9a55a] hover:bg-[#c9a55a]/10"
                    >
                      Select All
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={deselectAllCustomers}
                      className="border-[#2d5f3f] text-[#f5f3ed]/70 hover:bg-[#2d5f3f]/50"
                    >
                      Deselect All
                    </Button>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2 max-h-[200px] overflow-y-auto">
                  {customers.filter(c => c.email).map(customer => (
                    <div
                      key={customer.customer_id}
                      className={`flex items-center space-x-3 p-3 rounded-lg cursor-pointer transition-colors ${
                        selectedCustomers.includes(customer.customer_id)
                          ? 'bg-[#c9a55a]/20 border border-[#c9a55a]/50'
                          : 'bg-[#1a1f1a]/50 hover:bg-[#1a1f1a]/70'
                      }`}
                      onClick={() => toggleCustomer(customer.customer_id)}
                    >
                      <Checkbox
                        checked={selectedCustomers.includes(customer.customer_id)}
                        className="border-[#c9a55a] data-[state=checked]:bg-[#c9a55a]"
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-[#f5f3ed] text-sm truncate">
                          {customer.first_name} {customer.surname}
                        </p>
                        <p className="text-[#f5f3ed]/50 text-xs truncate">{customer.email}</p>
                      </div>
                    </div>
                  ))}
                </div>
                {customers.filter(c => !c.email).length > 0 && (
                  <p className="text-[#f5f3ed]/40 text-xs mt-2">
                    {customers.filter(c => !c.email).length} customer(s) hidden (no email)
                  </p>
                )}
              </div>
            )}

            {/* Template Selection */}
            <div className="bg-[#2d5f3f]/30 rounded-xl p-6 border border-[#2d5f3f]">
              <h2 className="text-lg font-semibold text-[#f5f3ed] mb-4">Email Template</h2>
              <Select value={selectedTemplate || "none"} onValueChange={(val) => setSelectedTemplate(val === "none" ? "" : val)}>
                <SelectTrigger className="bg-[#1a1f1a]/50 border-[#2d5f3f] text-[#f5f3ed]">
                  <SelectValue placeholder="Select a template or write custom email" />
                </SelectTrigger>
                <SelectContent className="bg-[#1a1f1a] border-[#2d5f3f]">
                  <SelectItem value="none" className="text-[#f5f3ed]/70">
                    -- Custom Email (No Template) --
                  </SelectItem>
                  {templates.map(template => (
                    <SelectItem
                      key={template.template_id}
                      value={template.template_id}
                      className="text-[#f5f3ed]"
                    >
                      {template.name}
                      {template.has_booking_placeholders && ' (Booking)'}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Subject & Body */}
            <div className="bg-[#2d5f3f]/30 rounded-xl p-6 border border-[#2d5f3f]">
              <h2 className="text-lg font-semibold text-[#f5f3ed] mb-4">Email Content</h2>
              <p className="text-[#f5f3ed]/50 text-xs mb-4">Type @ to insert variables</p>
              
              <div className="space-y-4">
                <div className="relative">
                  <Label className="text-[#f5f3ed]/70">Subject</Label>
                  <Input
                    ref={subjectRef}
                    value={subject}
                    onChange={handleSubjectChange}
                    onKeyDown={handleSubjectKeyDown}
                    onFocus={() => setInsertTarget('subject')}
                    placeholder="Email subject... (type @ for variables)"
                    className="bg-[#1a1f1a]/50 border-[#2d5f3f] text-[#f5f3ed]"
                    data-testid="email-subject"
                  />
                </div>
                <div className="relative">
                  <Label className="text-[#f5f3ed]/70">Body</Label>
                  <div 
                    className="bg-white rounded-lg overflow-hidden"
                    onFocus={() => setInsertTarget('body')}
                    onClick={() => setInsertTarget('body')}
                    onKeyDown={handleQuillKeyDown}
                  >
                    <ReactQuill
                      ref={quillRef}
                      theme="snow"
                      value={body}
                      onChange={handleQuillChange}
                      modules={modules}
                      formats={formats}
                      className="[&_.ql-container]:min-h-[250px] [&_.ql-container]:max-h-[400px] [&_.ql-container]:overflow-y-auto [&_.ql-editor]:min-h-[250px]"
                      data-testid="email-body"
                    />
                  </div>
                  {/* Table Tools */}
                  <div className="mt-2 flex flex-wrap gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setEditingTable(null);
                        setTableConfig({ rows: 3, cols: 3, showBorders: true, columnWidths: '' });
                        setShowTableModal(true);
                      }}
                      className="border-[#2d5f3f] text-[#f5f3ed]/70 hover:bg-[#2d5f3f]/30"
                    >
                      <Grid3X3 className="w-4 h-4 mr-2" />
                      Insert Table
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={toggleTableBorders}
                      className="border-[#2d5f3f] text-[#f5f3ed]/70 hover:bg-[#2d5f3f]/30"
                    >
                      Toggle Borders
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={addTableRow}
                      className="border-[#2d5f3f] text-[#f5f3ed]/70 hover:bg-[#2d5f3f]/30"
                    >
                      + Row
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={addTableColumn}
                      className="border-[#2d5f3f] text-[#f5f3ed]/70 hover:bg-[#2d5f3f]/30"
                    >
                      + Column
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={handleEditTable}
                      className="border-[#2d5f3f] text-[#f5f3ed]/70 hover:bg-[#2d5f3f]/30"
                    >
                      Edit Table
                    </Button>
                  </div>
                </div>
              </div>
            </div>

            {/* Attachments */}
            <div className="bg-[#2d5f3f]/30 rounded-xl p-6 border border-[#2d5f3f]">
              <h2 className="text-lg font-semibold text-[#f5f3ed] mb-4">Attachments</h2>
              {attachments.length === 0 ? (
                <p className="text-[#f5f3ed]/50 text-sm">No attachments available</p>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {attachments.map(attachment => (
                    <button
                      key={attachment.attachment_id}
                      type="button"
                      onClick={() => {
                        setSelectedAttachments(prev =>
                          prev.includes(attachment.attachment_id)
                            ? prev.filter(id => id !== attachment.attachment_id)
                            : [...prev, attachment.attachment_id]
                        );
                      }}
                      className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${
                        selectedAttachments.includes(attachment.attachment_id)
                          ? 'bg-[#c9a55a]/20 text-[#c9a55a] border border-[#c9a55a]'
                          : 'bg-[#1a1f1a]/50 text-[#f5f3ed]/70 border border-transparent hover:border-[#2d5f3f]'
                      }`}
                    >
                      <FileText className="w-4 h-4" />
                      {attachment.name}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-4">
              <Button
                onClick={handlePreview}
                disabled={previewing || selectedCustomers.length === 0}
                variant="outline"
                className="border-[#c9a55a] text-[#c9a55a] hover:bg-[#c9a55a]/10"
              >
                {previewing ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-[#c9a55a]" />
                ) : (
                  <>
                    <Eye className="w-5 h-5 mr-2" />
                    Preview
                  </>
                )}
              </Button>
              <Button
                onClick={handleSend}
                disabled={sending || selectedCustomers.length === 0}
                className="bg-[#c9a55a] hover:bg-[#b8944a] text-[#1a1f1a] font-semibold"
                data-testid="send-email-btn"
              >
                {sending ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-[#1a1f1a]" />
                ) : (
                  <>
                    <Send className="w-5 h-5 mr-2" />
                    Send Email{selectedCustomers.length > 1 ? 's' : ''}
                  </>
                )}
              </Button>
            </div>

            {/* Preview */}
            {preview && (
              <div className="bg-[#2d5f3f]/30 rounded-xl p-6 border border-[#2d5f3f]">
                <h2 className="text-lg font-semibold text-[#f5f3ed] mb-4">Preview</h2>
                <div className="bg-white rounded-lg p-6 text-gray-800">
                  <div className="mb-4 pb-4 border-b">
                    <p className="text-sm text-gray-500">To: {preview.customer_name} &lt;{preview.customer_email}&gt;</p>
                    <p className="font-semibold mt-1">{preview.subject}</p>
                  </div>
                  <div 
                    className="email-preview-content"
                    style={{
                      fontFamily: 'Arial, sans-serif',
                      lineHeight: '1.4',
                      color: '#333333'
                    }}
                    dangerouslySetInnerHTML={{ __html: preview.body }}
                  />
                </div>
              </div>
            )}

            {/* Send Result */}
            {sendResult && (
              <div className="bg-[#2d5f3f]/30 rounded-xl p-6 border border-[#2d5f3f]">
                <h2 className="text-lg font-semibold text-[#f5f3ed] mb-4">Send Results</h2>
                
                {sendResult.success.length > 0 && (
                  <div className="mb-4">
                    <h3 className="text-green-400 font-medium mb-2 flex items-center gap-2">
                      <CheckCircle className="w-5 h-5" />
                      Successfully Sent ({sendResult.success.length})
                    </h3>
                    <div className="space-y-1">
                      {sendResult.success.map((s, i) => (
                        <p key={i} className="text-[#f5f3ed]/70 text-sm">
                          {s.customer_name} ({s.email})
                        </p>
                      ))}
                    </div>
                  </div>
                )}
                
                {sendResult.failed.length > 0 && (
                  <div>
                    <h3 className="text-red-400 font-medium mb-2 flex items-center gap-2">
                      <XCircle className="w-5 h-5" />
                      Failed ({sendResult.failed.length})
                    </h3>
                    <div className="space-y-1">
                      {sendResult.failed.map((f, i) => (
                        <p key={i} className="text-[#f5f3ed]/70 text-sm">
                          {f.customer_name}: {f.error}
                        </p>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Placeholders Sidebar */}
          <div className="xl:col-span-1">
            <div className="bg-[#2d5f3f]/30 rounded-xl p-6 border border-[#2d5f3f] sticky top-4">
              <div className="flex items-center gap-2 mb-4">
                <Code className="w-5 h-5 text-[#c9a55a]" />
                <h2 className="text-lg font-semibold text-[#f5f3ed]">Placeholders</h2>
              </div>
              <p className="text-[#f5f3ed]/50 text-sm mb-2">Click to insert into:</p>
              <div className="flex gap-2 mb-4">
                <button
                  type="button"
                  onClick={() => setInsertTarget('subject')}
                  className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
                    insertTarget === 'subject' 
                      ? 'bg-[#c9a55a] text-[#1a1f1a]' 
                      : 'bg-[#1a1f1a]/50 text-[#f5f3ed]/70 hover:bg-[#1a1f1a]/70'
                  }`}
                >
                  Subject
                </button>
                <button
                  type="button"
                  onClick={() => setInsertTarget('body')}
                  className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
                    insertTarget === 'body' 
                      ? 'bg-[#c9a55a] text-[#1a1f1a]' 
                      : 'bg-[#1a1f1a]/50 text-[#f5f3ed]/70 hover:bg-[#1a1f1a]/70'
                  }`}
                >
                  Body
                </button>
              </div>

              <div className="mb-6">
                <h3 className="text-[#c9a55a] text-sm font-medium mb-2">Customer</h3>
                <div className="space-y-1 max-h-[200px] overflow-y-auto">
                  {customerPlaceholders.map(p => (
                    <button
                      key={p.field}
                      type="button"
                      onClick={() => insertPlaceholder(p.placeholder)}
                      className="w-full text-left px-2 py-1.5 text-xs hover:bg-[#c9a55a]/20 rounded group"
                      title={p.description}
                    >
                      <code className="text-[#c9a55a]">{p.placeholder}</code>
                      <span className="text-[#f5f3ed]/50 ml-2 group-hover:text-[#f5f3ed]/70">{p.description}</span>
                    </button>
                  ))}
                </div>
              </div>

              {isBookingContext && (
                <div>
                  <h3 className="text-blue-400 text-sm font-medium mb-2">Booking</h3>
                  <div className="space-y-1 max-h-[200px] overflow-y-auto">
                    {bookingPlaceholders.map(p => (
                      <button
                        key={p.field}
                        type="button"
                        onClick={() => insertPlaceholder(p.placeholder)}
                        className="w-full text-left px-2 py-1.5 text-xs hover:bg-blue-500/20 rounded group"
                        title={p.description}
                      >
                        <code className="text-blue-400">{p.placeholder}</code>
                        <span className="text-[#f5f3ed]/50 ml-2 group-hover:text-[#f5f3ed]/70">{p.description}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>

      {/* @ Mentions Dropdown */}
      {showMentions && filteredPlaceholders.length > 0 && (
        <div
          ref={mentionDropdownRef}
          className="fixed z-50 bg-[#1a1f1a] border border-[#2d5f3f] rounded-lg shadow-xl max-h-64 overflow-y-auto"
          style={{ top: mentionPosition.top, left: mentionPosition.left, minWidth: '250px' }}
        >
          {filteredPlaceholders.map((p, index) => (
            <button
              key={p.field}
              type="button"
              onClick={() => mentionSource === 'subject' ? insertMentionInSubject(p) : insertMentionInBody(p)}
              className={`w-full text-left px-3 py-2 flex flex-col hover:bg-[#2d5f3f]/50 transition-colors ${
                index === selectedMentionIndex ? 'bg-[#2d5f3f]/70' : ''
              }`}
            >
              <code className={`text-sm ${p.category === 'customer' ? 'text-[#c9a55a]' : 'text-blue-400'}`}>
                {p.placeholder}
              </code>
              <span className="text-[#f5f3ed]/50 text-xs">{p.description}</span>
            </button>
          ))}
        </div>
      )}

      {/* Table Insertion Modal */}
      <Dialog open={showTableModal} onOpenChange={setShowTableModal}>
        <DialogContent className="bg-[#1a1f1a] border-[#2d5f3f] text-[#f5f3ed]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Grid3X3 className="w-5 h-5 text-[#c9a55a]" />
              {editingTable ? 'Edit Table' : 'Insert Table'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-[#f5f3ed]/70">Rows</Label>
                <Input
                  type="number"
                  min="1"
                  max="20"
                  value={tableConfig.rows}
                  onChange={(e) => setTableConfig(prev => ({ ...prev, rows: parseInt(e.target.value) || 1 }))}
                  className="bg-[#1a1f1a]/50 border-[#2d5f3f] text-[#f5f3ed]"
                />
              </div>
              <div>
                <Label className="text-[#f5f3ed]/70">Columns</Label>
                <Input
                  type="number"
                  min="1"
                  max="10"
                  value={tableConfig.cols}
                  onChange={(e) => setTableConfig(prev => ({ ...prev, cols: parseInt(e.target.value) || 1 }))}
                  className="bg-[#1a1f1a]/50 border-[#2d5f3f] text-[#f5f3ed]"
                />
              </div>
            </div>
            
            <div>
              <Label className="text-[#f5f3ed]/70">Column Widths (optional)</Label>
              <Input
                value={tableConfig.columnWidths}
                onChange={(e) => setTableConfig(prev => ({ ...prev, columnWidths: e.target.value }))}
                placeholder="e.g., 100,200,150 or 30%,40%,30%"
                className="bg-[#1a1f1a]/50 border-[#2d5f3f] text-[#f5f3ed]"
              />
              <p className="text-[#f5f3ed]/50 text-xs mt-1">
                Comma-separated values in pixels or percentages
              </p>
            </div>
            
            <div className="flex items-center gap-2">
              <Checkbox
                id="showBorders"
                checked={tableConfig.showBorders}
                onCheckedChange={(checked) => setTableConfig(prev => ({ ...prev, showBorders: checked }))}
                className="border-[#2d5f3f] data-[state=checked]:bg-[#c9a55a]"
              />
              <Label htmlFor="showBorders" className="text-[#f5f3ed]/70 cursor-pointer">
                Show gridlines/borders
              </Label>
            </div>
            
            {/* Preview */}
            <div className="mt-4">
              <Label className="text-[#f5f3ed]/70 mb-2 block">Preview</Label>
              <div className="bg-white rounded p-4 overflow-x-auto">
                <table style={{ borderCollapse: 'collapse', width: '100%' }}>
                  <tbody>
                    {Array.from({ length: Math.min(tableConfig.rows, 5) }).map((_, r) => (
                      <tr key={r}>
                        {Array.from({ length: Math.min(tableConfig.cols, 5) }).map((_, c) => {
                          const widths = tableConfig.columnWidths.split(',').map(w => w.trim()).filter(Boolean);
                          const width = widths[c] || '';
                          const widthStyle = width ? { width: width.includes('%') ? width : `${width}px` } : {};
                          return (
                            <td
                              key={c}
                              style={{
                                border: tableConfig.showBorders ? '1px solid #ccc' : 'none',
                                padding: '8px',
                                backgroundColor: '#f9f9f9',
                                ...widthStyle
                              }}
                            >
                              &nbsp;
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
                {(tableConfig.rows > 5 || tableConfig.cols > 5) && (
                  <p className="text-gray-500 text-xs mt-2">Preview limited to 5x5</p>
                )}
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowTableModal(false)}
              className="border-[#2d5f3f] text-[#f5f3ed]"
            >
              Cancel
            </Button>
            <Button
              onClick={insertTable}
              className="bg-[#c9a55a] hover:bg-[#b8944a] text-[#1a1f1a]"
            >
              Insert Table
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SendEmail;
